import React, { useMemo, useState } from 'react';
import clsx from 'clsx';
import dayjs from 'dayjs';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import SvgPlus from '@icons/plus.svg?react';
import SvgEdit from '@icons/pencil-inverted.svg?react';
import SvgChevron from '@icons/chevron-thin.svg?react';
import SvgWarning from '@icons/alert.svg?react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { PriceTableForm } from '@components/ringil3/Features/Contracted/types.ts';
import { planCsv, timelineRows } from '@utils/contractedPriceTable.ts';

interface ComponentProps {
  table: PriceTableForm;
  /** Opens the plan sidebar; `addPeriod` = with a new empty period. */
  onEditPlan?: (addPeriod: boolean) => void;
  readOnly?: boolean;
  /** Day the prices are taken for (a shipment: its deadline day); marked on the timeline when it differs from today. */
  pricingDate?: string;
  /** ISO day, injectable for tests; defaults to today. */
  today?: string;
  /** Name for the downloaded history file. */
  exportName?: string;
}

const COLLAPSED_ROWS = 3;
const MIN_MONTHS = 4;
// Excel needs the byte order mark to read the CSV as UTF-8
const BOM = String.fromCharCode(0xfeff);
/** label columns | timeline | edit button */
const GRID = 'grid grid-cols-[minmax(10rem,2fr)_minmax(0,5fr)_2.5rem] gap-x-3';

const shortDate = (date: string, withYear: boolean) => dayjs(date).format(withYear ? 'D. M. YYYY' : 'D. M.');

/**
 * "Cenové úrovně": the plan from today on as rows (level, period, days) with bars on a month grid. The current period
 * is green, a gap of the plan is a red "Nezadáno". No plan at all shows the "Naplánovat" call to action.
 */
const PriceLevelTimeline: React.FC<ComponentProps> = ({ table, onEditPlan, readOnly = false, pricingDate, today = dayjs().format('YYYY-MM-DD'), exportName = 'plan' }) => {
  const { tr } = useTranslationLgs();
  const [expanded, setExpanded] = useState(false);

  const rows = useMemo(() => timelineRows(table, today), [table, today]);
  const hasPlan = (table?.intervals ?? []).some(i => i.validFrom && i.validTo);

  const start = dayjs(today).startOf('month');
  const lastDay = rows.length ? rows[rows.length - 1].to : today;
  // the deadline day is marked too, so the grid reaches it even past the end of the plan
  const reachDay = pricingDate && pricingDate > lastDay ? pricingDate : lastDay;
  const planEnd = dayjs(reachDay).endOf('month');
  const minEnd = start.add(MIN_MONTHS - 1, 'month').endOf('month');
  const end = planEnd.isAfter(minEnd) ? planEnd : minEnd;
  const totalDays = end.diff(start, 'day') + 1;
  const offset = (date: string) => (Math.min(totalDays, Math.max(0, dayjs(date).diff(start, 'day'))) / totalDays) * 100;
  const months: dayjs.Dayjs[] = [];
  for (let m = start; m.isBefore(end); m = m.add(1, 'month')) months.push(m);

  // Czech forms picked here (1 den, 2–4 dny, 5+ dní) - they must not depend on plural keys being present in Locize
  const days = (count: number, current: boolean) => {
    const form = count === 1 ? 'One' : count >= 2 && count <= 4 ? 'Few' : 'Many';
    const defaults = {
      One: current ? 'zbývá {{count}} den' : '{{count}} den',
      Few: current ? 'zbývají {{count}} dny' : '{{count}} dny',
      Many: current ? 'zbývá {{count}} dní' : '{{count}} dní',
    };
    return tr(`PricePlan.${current ? 'daysLeft' : 'days'}${form}`, defaults[form], { count });
  };

  const downloadPlan = () => {
    const csv = planCsv(table, [tr('PricePlan.csvLevel', 'Úroveň'), tr('PricePlan.validFrom', 'Platí od'), tr('PricePlan.validTo', 'Platí do')]);
    const url = URL.createObjectURL(new Blob([BOM + csv], { type: 'text/csv;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `${exportName}-plan.csv`;
    link.click();
    // revoked later: Firefox and Safari start the download asynchronously
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const header = (
    <div className={'flex flex-row items-center justify-between'}>
      <div className={'text-lg'}>{tr('PricePlan.levels', 'Cenové úrovně')}</div>
      {hasPlan && (
        <RingilButton mode={'text-only'} label={tr('PricePlan.downloadHistory', 'Stáhnout historická data')} onClick={downloadPlan} dataTestId={'price-plan-history'} />
      )}
    </div>
  );

  if (rows.length === 0) {
    return (
      <div className={'flex flex-col gap-3'} data-testid={'price-plan-timeline'}>
        {header}
        <div className={'flex flex-row items-center justify-between gap-4 rounded bg-complementary-errorMedium px-4 py-3'}>
          <div className={'flex flex-row items-center gap-3 text-sm'}>
            <SvgWarning className={'w-4 h-4 text-content-secondary'} />
            {tr('PricePlan.empty', 'Zatím není naplánovaná žádná cenová úroveň. Naplánujte první interval.')}
          </div>
          {!readOnly && onEditPlan && (
            <RingilButton mode={'outlined'} label={tr('PricePlan.schedule', 'Naplánovat')} onClick={() => onEditPlan(true)} dataTestId={'price-plan-schedule'} />
          )}
        </div>
      </div>
    );
  }

  const visibleRows = expanded ? rows : rows.slice(0, COLLAPSED_ROWS);
  const todayLeft = offset(today);
  const pricingLeft = pricingDate && pricingDate !== today ? offset(pricingDate) : null;
  const currentMonth = dayjs(today).format('YYYY-MM');

  return (
    <div className={'flex flex-col gap-2'} data-testid={'price-plan-timeline'}>
      {header}
      <div>
        {/* month header */}
        <div className={clsx(GRID, 'items-end')}>
          <div />
          <div className={'flex flex-row text-sm'}>
            {months.map(m => (
              <div
                key={m.format('YYYY-MM')}
                style={{ width: `${(m.daysInMonth() / totalDays) * 100}%` }}
                className={clsx('pl-1 pb-1 truncate', m.format('YYYY-MM') === currentMonth ? 'font-medium text-content-primary' : 'text-content-secondary')}
              >
                {m.format(m.month() === 0 || m.year() !== start.year() ? 'MMMM YYYY' : 'MMMM').replace(/^./, c => c.toUpperCase())}
              </div>
            ))}
          </div>
          <div />
        </div>

        {/* rows: one line between them; month lines and the today line run through the whole timeline column */}
        <div className={'relative'}>
          <div className={clsx(GRID, 'pointer-events-none absolute inset-0')}>
            <div />
            <div className={'relative'}>
              {months.slice(1).map(m => (
                <div key={m.format('YYYY-MM')} className={'absolute top-0 bottom-0 w-px bg-content-quaternary'} style={{ left: `${offset(m.format('YYYY-MM-DD'))}%` }} />
              ))}
              <div className={'absolute top-0 bottom-0 w-0.5 bg-content-brand z-10'} style={{ left: `${todayLeft}%` }} />
              {/* deadline day: an even dashed stripe of the same width as the today line (a dashed border breaks up row by row) */}
              {pricingLeft !== null && (
                <div
                  className={'absolute top-0 bottom-0 w-0.5 z-10 text-content-brand'}
                  style={{ left: `${pricingLeft}%`, backgroundImage: 'repeating-linear-gradient(to bottom, currentColor 0 5px, transparent 5px 9px)' }}
                  data-testid={'price-plan-deadline-line'}
                />
              )}
            </div>
            <div />
          </div>

          {visibleRows.map(row => {
            const gap = row.levelKey === null;
            const withYear = dayjs(row.from).year() !== dayjs(today).year() || dayjs(row.to).year() !== dayjs(today).year();
            return (
              <div key={row.key} className={clsx(GRID, 'items-center border-0 border-b border-solid border-content-quaternary')} data-testid={'price-plan-row'}>
                <div className={clsx('grid grid-cols-[1rem_1fr_1fr_1fr] gap-2 items-center py-2 text-sm', gap && 'text-complementary-error')}>
                  {gap ? (
                    <SvgWarning className={'w-3 h-3 text-complementary-error'} />
                  ) : (
                    <span className={clsx('inline-block w-2.5 h-2.5 rounded-full', row.current ? 'bg-complementary-success' : 'bg-content-tertiary')} />
                  )}
                  <span className={'truncate'}>{gap ? tr('PricePlan.notSet', 'Nezadáno') : row.levelName}</span>
                  <span className={'whitespace-nowrap'}>
                    {shortDate(row.from, withYear)} – {shortDate(row.to, withYear)}
                  </span>
                  <span className={'whitespace-nowrap'}>{days(row.days, row.current)}</span>
                </div>
                <div className={'relative h-9'}>
                  <div className={'absolute left-0 right-0 top-1/2 h-1 -translate-y-1/2 rounded bg-content-quaternary opacity-40'} />
                  <div
                    className={clsx(
                      'absolute top-1/2 h-2.5 -translate-y-1/2 rounded-sm',
                      gap ? 'bg-complementary-error' : row.current ? 'bg-complementary-success' : 'bg-content-tertiary',
                    )}
                    style={{ left: `${offset(row.from)}%`, width: `${Math.max(0.8, ((dayjs(row.to).diff(dayjs(row.from), 'day') + 1) / totalDays) * 100)}%` }}
                  />
                </div>
                <div className={'flex justify-end py-1'}>
                  {!readOnly && onEditPlan && (
                    <RingilButton mode={'table'} icon={<SvgEdit className={'w-4 h-4'} />} tooltip={tr('PricePlan.edit', 'Upravit plán')} onClick={() => onEditPlan(false)} />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className={'flex flex-row items-center gap-8 pt-2'}>
        {rows.length > COLLAPSED_ROWS && (
          <button
            type={'button'}
            className={'flex flex-row items-center gap-2 bg-transparent border-0 p-0 cursor-pointer text-content-brand'}
            onClick={() => setExpanded(!expanded)}
            data-testid={'price-plan-toggle'}
          >
            {expanded ? tr('PricePlan.hidePlan', 'Skrýt plán') : tr('PricePlan.morePlan', 'Další plán')}
            <SvgChevron className={clsx('w-3 h-3 transition-transform', expanded && 'rotate-180')} />
          </button>
        )}
        {!readOnly && onEditPlan && (
          <button
            type={'button'}
            className={'flex flex-row items-center gap-2 bg-transparent border-0 p-0 cursor-pointer text-content-brand'}
            onClick={() => onEditPlan(true)}
            data-testid={'price-plan-add-interval'}
          >
            <SvgPlus className={'w-4 h-4'} />
            {tr('PricePlan.addInterval', 'Přidat interval')}
          </button>
        )}
      </div>
    </div>
  );
};

export default PriceLevelTimeline;
